import React, { useState, useEffect, useCallback } from 'react';
import './NpsResults.scss';
import { getNpsResponses } from '../../../api/Nps/npsApi';
import { INpsResponse, INpsResponsesResult } from '../../../types/nps';
import { Breadcrumbs, Button } from '@orderly/morrisons-component-library';
import { navigateTo } from '../../../routing/Navigation';
import LoadingThrobber from '../../../components/LoadingThrobber/LoadingThrobber';
import Table from '../../../components/Table/Table';

interface INpsResultsState {
  data: INpsResponsesResult | null;
  isLoading: boolean;
  error: string | null;
  fromDate: string;
  toDate: string;
  retailerFilter: string;
  page: number;
  sortBy: string;
  sortDirection: string;
}

const formatTriggerPoint = (value: number): string => {
  switch (value) {
    case 0: return 'Before Order';
    case 1: return 'After Order';
    case 2: return 'Manual';
    default: return String(value);
  }
};

const getScoreClass = (score: number): string => {
  if (score <= 6) {
    return 'nps-score nps-score--detractor';
  }
  if (score <= 8) {
    return 'nps-score nps-score--passive';
  }
  return 'nps-score nps-score--promoter';
};

const NpsResults: React.FC = () => {
  const [state, setState] = useState<INpsResultsState>({
    data: null,
    isLoading: false,
    error: null,
    fromDate: '',
    toDate: '',
    retailerFilter: '',
    page: 1,
    sortBy: 'date',
    sortDirection: 'desc',
  });

  const [searchVersion, setSearchVersion] = useState(0);

  const loadData = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await getNpsResponses(
        state.fromDate || undefined,
        state.toDate || undefined,
        state.retailerFilter || undefined,
        state.page,
        20,
        state.sortBy,
        state.sortDirection,
      );
      setState(prev => ({ ...prev, data: result, isLoading: false }));
    } catch {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load NPS results. Please try again.',
      }));
    }
  }, [
    state.fromDate, state.toDate, state.retailerFilter,
    state.page, state.sortBy, state.sortDirection,
  ]);

  useEffect(() => {
    // tslint:disable-next-line:no-floating-promises
    loadData();
  }, [loadData, searchVersion]);

  const handleSearch = () => {
    setState(prev => ({ ...prev, page: 1 }));
    setSearchVersion(v => v + 1);
  };

  const handleSort = (column: string) => {
    setState((prev) => {
      const isSame = prev.sortBy === column;
      return {
        ...prev,
        sortBy: column,
        sortDirection: isSame && prev.sortDirection === 'asc'
          ? 'desc' : 'asc',
        page: 1,
      };
    });
    setSearchVersion(v => v + 1);
  };

  const getSortIndicator = (column: string): string => {
    if (state.sortBy !== column) {
      return '';
    }
    return state.sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleExportCsv = async () => {
    if (!state.data || state.data.totalCount === 0) {
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    let exportItems: INpsResponse[];
    try {
      const fullResult = await getNpsResponses(
        state.fromDate || undefined,
        state.toDate || undefined,
        state.retailerFilter || undefined,
        1,
        state.data.totalCount,
        state.sortBy,
        state.sortDirection,
      );
      exportItems = fullResult.items;
    } catch {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to export NPS results. Please try again.',
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: false }));

    const escapeCsv = (value: string | number | null | undefined): string => {
      const stringValue = value === null || value === undefined ? '' : String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const headers = [
      'Feedback submitted date and time',
      'Retailer name',
      'Retailer email address',
      'Retailer NPS score (0–10)',
      'Retailer detailed feedback',
    ];
    const formatDateForCsv = (value: string | Date): string => {
      const d = new Date(value);
      const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    // tslint:disable-next-line:arrow-parens
    const rows = exportItems.map((item: INpsResponse) => [
      escapeCsv(formatDateForCsv(item.submittedAt)),
      escapeCsv(item.retailerName || String(item.retailerId)),
      escapeCsv(item.userEmail),
      escapeCsv(item.score),
      escapeCsv(item.feedback),
    ]);

    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map(r => r.join(',')),
    ].join('\r\n');
    const blob = new Blob(
      [csvContent], { type: 'text/csv;charset=utf-8;' },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const datePart = new Date().toISOString().split('T')[0];
    link.download = `nps-results-${datePart}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = state.data
    ? Math.ceil(state.data.totalCount / state.data.pageSize)
    : 0;

  return (
    <div className="admin-nps-results">
      <Breadcrumbs
        onHomeClick={() => navigateTo('/admin/home')}
        onRedirect={() => navigateTo(window.location.pathname)}
        segments={[
          { key: 0, text: 'NPS Results', url: '' },
        ]}
      />

      {state.isLoading && <LoadingThrobber />}

      <section className="heading-wrapper">
        <h1>NPS Results</h1>
        <div className="nps-actions">
          <Button
            type="button"
            className="primary"
            text={state.isLoading ? 'Loading...' : 'Search'}
            onClick={handleSearch}
            disabled={state.isLoading}
          />
          <Button
            type="button"
            className="secondary"
            text="Export CSV"
            onClick={handleExportCsv}
            disabled={
              !state.data || state.data.items.length === 0
            }
          />
        </div>
      </section>

      <div className="content-wrapper">
        <div className="nps-filters">
          <div className="nps-filter-group">
            <label htmlFor="nps-from-date">From Date</label>
            <input
              id="nps-from-date"
              type="date"
              value={state.fromDate}
              onChange={(e) => {
                const val = e.target.value;
                setState(prev => ({ ...prev, fromDate: val }));
              }}
            />
          </div>
          <div className="nps-filter-group">
            <label htmlFor="nps-to-date">To Date</label>
            <input
              id="nps-to-date"
              type="date"
              value={state.toDate}
              onChange={(e) => {
                const val = e.target.value;
                setState(prev => ({ ...prev, toDate: val }));
              }}
            />
          </div>
          <div className="nps-filter-group">
            <label htmlFor="nps-retailer">Retailer</label>
            <input
              id="nps-retailer"
              type="text"
              placeholder="Search by retailer"
              value={state.retailerFilter}
              onChange={(e) => {
                const val = e.target.value;
                setState(prev => ({ ...prev, retailerFilter: val }));
              }}
            />
          </div>
        </div>

        {state.error && !state.isLoading && (
          <p className="nps-error">{state.error}</p>
        )}

        {state.data && !state.isLoading && (
          <>
            <p className="nps-summary">
              Showing {state.data.items.length} of{' '}
              {state.data.totalCount} results
            </p>

            <Table className="nps-table">
              <thead>
                <tr>
                  <th
                    className="nps-sortable"
                    onClick={() => handleSort('date')}
                  >
                    Submitted Date{getSortIndicator('date')}
                  </th>
                  <th
                    className="nps-sortable"
                    onClick={() => handleSort('retailer')}
                  >
                    Retailer{getSortIndicator('retailer')}
                  </th>
                  <th>Email</th>
                  <th
                    className="nps-sortable"
                    onClick={() => handleSort('score')}
                  >
                    Score{getSortIndicator('score')}
                  </th>
                  <th>Feedback (optional)</th>
                  <th
                    className="nps-sortable"
                    onClick={() => handleSort('trigger')}
                  >
                    Trigger{getSortIndicator('trigger')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.data.items.length > 0 &&
                  state.data.items.map(
                    // tslint:disable-next-line:arrow-parens
                    (item: INpsResponse) => (
                      <tr key={item.id}>
                        <td>
                          {new Date(item.submittedAt).toLocaleString()}
                        </td>
                        <td>
                          {item.retailerName || item.retailerId}
                        </td>
                        <td>
                          {item.userEmail || (
                            <span className="nps-empty-cell">
                              &mdash;
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={getScoreClass(item.score)}>
                            {item.score}
                          </span>
                        </td>
                        <td>
                          {item.feedback || (
                            <span className="nps-empty-cell">
                              &mdash;
                            </span>
                          )}
                        </td>
                        <td>
                          {formatTriggerPoint(item.triggerPoint)}
                        </td>
                      </tr>
                    ),
                  )}
                {state.data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="nps-no-records">
                      No NPS responses found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="nps-pagination">
                <Button
                  type="button"
                  className="secondary"
                  text="Previous"
                  onClick={() => setState(prev => ({
                    ...prev, page: Math.max(1, prev.page - 1),
                  }))}
                  disabled={state.page <= 1}
                />
                <span className="nps-page-info">
                  Page {state.page} of {totalPages}
                </span>
                <Button
                  type="button"
                  className="secondary"
                  text="Next"
                  onClick={() => setState(prev => ({
                    ...prev,
                    page: Math.min(totalPages, prev.page + 1),
                  }))}
                  disabled={state.page >= totalPages}
                />
              </div>
            )}
          </>
        )}

        {!state.data && !state.isLoading && !state.error && (
          <p className="nps-no-records">No records found.</p>
        )}
      </div>
    </div>
  );
};

export default NpsResults;
